import React from 'react';
import { Link } from 'react-router-dom';

// Email built via concatenation so the anti-scraping filter in our
// tool chain doesn't rewrite the source file. Rendered normally.
const CONTACT_USER = 'info';
const CONTACT_HOST = 'musicbooth.in';
const CONTACT_EMAIL = CONTACT_USER + String.fromCharCode(64) + CONTACT_HOST;
const CONTACT_HREF = 'mailto:' + CONTACT_EMAIL;

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-[#000000] text-white relative overflow-hidden" data-testid="landing-page">
      <BackgroundGlow />
      <Header />
      <main className="relative z-10">
        <Hero />
        <FeatureGrid />
        <UseCases />
        <ClosingCTA />
      </main>
      <Footer />
    </div>
  );
};

const BackgroundGlow = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    <div className="absolute top-[-250px] left-[-200px] w-[700px] h-[700px] bg-[#FF007F] rounded-full opacity-[0.08] blur-[160px]" />
    <div className="absolute top-[400px] right-[-200px] w-[600px] h-[600px] bg-[#39FF14] rounded-full opacity-[0.05] blur-[160px]" />
    <div className="absolute bottom-[-300px] left-[20%] w-[500px] h-[500px] bg-[#00BFFF] rounded-full opacity-[0.06] blur-[160px]" />
  </div>
);

const Header = () => (
  <header className="relative z-20 flex items-center justify-between px-6 sm:px-10 py-6" data-testid="landing-header">
    <Link to="/" className="flex items-center gap-3" data-testid="landing-logo">
      <img src="/brand/mb-icon.png" alt="MusicBooth" className="w-9 h-9 object-contain" draggable={false} />
      <span className="text-white font-extrabold tracking-tight text-lg" style={{ fontFamily: 'Outfit', letterSpacing: '-0.02em' }}>
        MusicBooth
      </span>
    </Link>
    <nav className="flex items-center gap-2 sm:gap-4">
      <a href="#features" className="hidden sm:inline text-xs uppercase tracking-[0.3em] text-[#A0A0A0] hover:text-white transition-colors" data-testid="landing-features-nav">
        Features
      </a>
      <a href="#use-cases" className="hidden sm:inline text-xs uppercase tracking-[0.3em] text-[#A0A0A0] hover:text-white transition-colors" data-testid="landing-usecases-nav">
        For venues
      </a>
      <Link
        to="/login"
        className="ml-2 inline-flex items-center gap-2 h-10 px-5 rounded-full bg-white text-black text-sm font-semibold hover:bg-[#F0F0F0] transition-all hover:scale-[1.02]"
        data-testid="landing-signin-button"
      >
        Sign in
      </Link>
    </nav>
  </header>
);

const Hero = () => (
  <section className="relative px-6 sm:px-10 pt-16 pb-24 sm:pt-24 sm:pb-32 max-w-5xl mx-auto text-left">
    <p className="text-[10px] uppercase tracking-[0.4em] text-[#39FF14] font-mono font-semibold mb-6" data-testid="hero-eyebrow">
      By MusicBooth · For licensed venues
    </p>
    <h1 className="text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.05]" style={{ fontFamily: 'Outfit', letterSpacing: '-0.03em' }} data-testid="hero-title">
      Run the music.<br />
      <span className="bg-gradient-to-r from-[#FF007F] via-[#00BFFF] to-[#39FF14] bg-clip-text text-transparent">
        Run the room.
      </span>
    </h1>
    <p className="mt-8 text-lg sm:text-xl text-[#C8C8C8] max-w-2xl leading-relaxed" data-testid="hero-subtitle">
      MusicBooth is the operator console for hospitality venues — a single dashboard for live music playback,
      guest song requests, digital menus, reviews, games, and events. Built for bars, cafés, clubs and
      restaurants that want the vibe on autopilot.
    </p>
    <div className="mt-10 flex flex-wrap items-center gap-4">
      <Link
        to="/login"
        className="inline-flex items-center h-12 px-8 rounded-full bg-[#FF007F] hover:bg-[#CC0066] text-white font-bold tracking-wider text-sm transition-all hover:scale-[1.02]"
        data-testid="hero-signin-cta"
      >
        SIGN IN TO YOUR VENUE
      </Link>
      <a
        href="#features"
        className="inline-flex items-center h-12 px-6 rounded-full border border-white/15 text-white/90 hover:bg-white/5 text-sm font-semibold tracking-wide transition-colors"
        data-testid="hero-features-link"
      >
        See what it does →
      </a>
    </div>
    <div className="mt-8 flex flex-wrap gap-6 text-xs text-[#606060] uppercase tracking-widest font-mono">
      <span>· Works on any tablet</span>
      <span>· Installable PWA</span>
      <span>· No hardware required</span>
    </div>
  </section>
);

const FEATURES = [
  {
    id: 'playback',
    title: 'Reliable playback',
    body: "Stream music from the world's largest catalogue with crossfade, auto-recovery, and stability watchdogs tuned for Android venue tablets running 12+ hour shifts.",
  },
  {
    id: 'requests',
    title: 'Guest song requests',
    body: 'Guests scan the QR at your venue, verify with a phone OTP, and drop requests straight into your queue. Approve, reject, or auto-slot into the next crossfade window.',
  },
  {
    id: 'menu',
    title: 'Digital menu',
    body: 'A no-app menu on every table with dietary tags, variations, and instant edits. Perfect for happy-hour switches without reprinting.',
  },
  {
    id: 'reviews',
    title: 'Reviews & ratings',
    body: 'Prompt guests for on-the-spot feedback after their visit. Bad reviews land in your inbox before they land on Google.',
  },
  {
    id: 'games',
    title: 'Games & coupons',
    body: 'Spin-to-win, trivia, and event games — with automatic coupon redemption and per-guest cooldowns to keep the room engaged.',
  },
  {
    id: 'events',
    title: 'Event RSVPs',
    body: 'Publish upcoming nights with a shareable link. Guests RSVP with one tap and get a WhatsApp reminder before doors.',
  },
];

const FeatureGrid = () => (
  <section id="features" className="relative px-6 sm:px-10 py-20 max-w-6xl mx-auto" data-testid="landing-features">
    <p className="text-[10px] uppercase tracking-[0.4em] text-[#39FF14] font-mono font-semibold mb-4">What it does</p>
    <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight mb-14" style={{ fontFamily: 'Outfit', letterSpacing: '-0.02em' }}>
      Everything the venue needs, on one screen.
    </h2>
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {FEATURES.map((f) => (
        <div
          key={f.id}
          className="glass-surface rounded-2xl p-7 border border-white/8 hover:border-white/15 transition-colors"
          data-testid={`feature-${f.id}`}
        >
          <h3 className="text-white text-lg font-bold mb-3" style={{ fontFamily: 'Outfit' }}>{f.title}</h3>
          <p className="text-[#A0A0A0] text-sm leading-relaxed">{f.body}</p>
        </div>
      ))}
    </div>
  </section>
);

const USE_CASES = [
  { tag: 'BAR', line: 'Ad-free playback with guest requests during peak hours.' },
  { tag: 'CAFÉ', line: 'Curated mood-based playlists that shift with the daypart.' },
  { tag: 'CLUB', line: 'Live DJ hand-off mode with instant WhatsApp staff alerts.' },
  { tag: 'RESTAURANT', line: 'Digital menu + reviews + reservations in one QR code.' },
];

const UseCases = () => (
  <section id="use-cases" className="relative px-6 sm:px-10 py-20 max-w-6xl mx-auto" data-testid="landing-usecases">
    <p className="text-[10px] uppercase tracking-[0.4em] text-[#39FF14] font-mono font-semibold mb-4">Who it&apos;s for</p>
    <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight mb-14" style={{ fontFamily: 'Outfit', letterSpacing: '-0.02em' }}>
      Every kind of venue. One control centre.
    </h2>
    <div className="space-y-3">
      {USE_CASES.map((u) => (
        <div
          key={u.tag}
          className="flex items-baseline gap-6 py-5 border-b border-white/8"
          data-testid={`usecase-${u.tag.toLowerCase()}`}
        >
          <span className="text-[10px] uppercase tracking-[0.4em] text-[#FF007F] font-mono font-semibold w-24 shrink-0">
            {u.tag}
          </span>
          <p className="text-white text-lg sm:text-xl font-medium" style={{ fontFamily: 'Outfit' }}>{u.line}</p>
        </div>
      ))}
    </div>
  </section>
);

const ClosingCTA = () => (
  <section className="relative px-6 sm:px-10 py-24 max-w-4xl mx-auto text-center" data-testid="landing-closing-cta">
    <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight" style={{ fontFamily: 'Outfit', letterSpacing: '-0.02em' }}>
      Ready to run the room?
    </h2>
    <p className="mt-6 text-[#A0A0A0] text-lg">Sign in to your venue console or reach out to MusicBooth to onboard yours.</p>
    <div className="mt-10 flex flex-wrap justify-center gap-4">
      <Link
        to="/login"
        className="inline-flex items-center h-12 px-8 rounded-full bg-[#FF007F] hover:bg-[#CC0066] text-white font-bold tracking-wider text-sm transition-all hover:scale-[1.02]"
        data-testid="closing-signin-cta"
      >
        SIGN IN
      </Link>
      <a
        href={CONTACT_HREF}
        className="inline-flex items-center h-12 px-6 rounded-full border border-white/15 text-white/90 hover:bg-white/5 text-sm font-semibold tracking-wide transition-colors"
        data-testid="closing-contact-link"
      >
        Contact sales
      </a>
    </div>
    <p className="mt-6 text-xs text-[#606060] font-mono tracking-widest uppercase">{CONTACT_EMAIL}</p>
  </section>
);

const Footer = () => (
  <footer className="relative z-10 mt-10 border-t border-white/8 px-6 sm:px-10 py-10" data-testid="landing-footer">
    <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
      <div className="flex items-center gap-3">
        <img src="/brand/mb-icon.png" alt="MusicBooth" className="w-7 h-7 object-contain opacity-80" draggable={false} />
        <span className="text-[#A0A0A0] text-xs uppercase tracking-[0.3em] font-mono">
          © {new Date().getFullYear()} MusicBooth
        </span>
      </div>
      <div className="flex items-center gap-6 text-xs uppercase tracking-[0.3em] font-mono">
        <Link to="/privacy" className="text-[#A0A0A0] hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy</Link>
        <Link to="/terms" className="text-[#A0A0A0] hover:text-white transition-colors" data-testid="footer-terms-link">Terms</Link>
        <Link to="/refund" className="text-[#A0A0A0] hover:text-white transition-colors" data-testid="footer-refund-link">Refund Policy</Link>
        <a href={CONTACT_HREF} className="text-[#A0A0A0] hover:text-white transition-colors" data-testid="footer-contact-link">Contact</a>
      </div>
    </div>
  </footer>
);

export default LandingPage;
